import React, { useState, useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledJobsSection = styled.section`
  max-width: 900px;
  margin: 0 auto;
  margin-bottom: 120px;
`;

const StyledAccordion = styled.div`
  margin-top: 50px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyledAccordionItem = styled.div`
  background: var(--surface-light);
  border: 1px solid var(--text-dark);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  transition: var(--transition);

  &:hover {
    border-color: var(--accent-main);
  }

  &:hover {
    border-color: var(--accent-main);
  }
`;

const StyledAccordionHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 30px 40px 10px 40px;
  background: transparent;
  color: var(--text-main);
  border: none;
  text-align: left;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px 30px 10px 30px;
  }

  @media (max-width: 480px) {
    padding: 15px 20px 5px 20px;
  }

  .header-content {
    display: flex;
    flex-direction: column;
    
    h3 {
      font-size: var(--fz-xl);
      font-weight: 700;
      margin: 0;
      color: var(--accent-main);
      transition: var(--transition);
      
      .company {
        color: var(--text-main);
      }
    }

    .range {
      margin-top: 10px;
      color: var(--text-muted);
      font-family: var(--font-mono);
      font-size: var(--fz-sm);
    }
  }

`;

const StyledAccordionContent = styled.div`
  overflow: visible;

  .content-inner {
    padding: 0 40px 40px;

    @media (max-width: 768px) {
      padding: 0 30px 30px;
    }

    @media (max-width: 480px) {
      padding: 0 20px 20px;
    }

    ul {
      ${({ theme }) => theme.mixins.fancyList};
      
      li {
        color: var(--text-muted);
        font-size: var(--fz-lg);
        line-height: 1.6;
        margin-bottom: 15px;
      }
    }
  }
`;

const Jobs = () => {
  const data = useStaticQuery(graphql`
    query {
      jobs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/jobs/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              company
              location
              range
              url
            }
            html
          }
        }
      }
    }
  `);

  const jobsData = data.jobs.edges;

  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealContainer.current, srConfig());
  }, []);


  return (
    <StyledJobsSection id="jobs" ref={revealContainer}>
      <h2 className="medium-heading">Experience</h2>

      <StyledAccordion>
        {jobsData &&
          jobsData.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { title, url, company, range } = frontmatter;
            return (
              <StyledAccordionItem key={i}>
                <StyledAccordionHeader>
                  <div className="header-content">
                    <h3>
                      <span>{title}</span>
                      <span className="company">
                        &nbsp;@&nbsp;
                        <a href={url} className="inline-link" target="_blank" rel="noopener noreferrer">
                          {company}
                        </a>
                      </span>
                    </h3>
                    <span className="range">{range}</span>
                  </div>
                </StyledAccordionHeader>
                <StyledAccordionContent>
                  <div className="content-inner">
                    <div dangerouslySetInnerHTML={{ __html: html }} />
                  </div>
                </StyledAccordionContent>
              </StyledAccordionItem>
            );
          })}
      </StyledAccordion>
    </StyledJobsSection>
  );
};

export default Jobs;
